import { useState } from "react";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Send,
  CheckCircle,
  Loader2
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    project_type: "",
    project_duration: "",
    project_details: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const projectTypes = [
    { value: "mobile_app", label: "Mobil Uygulama" },
    { value: "web_development", label: "Web Sitesi / Web Uygulama" },
    { value: "n8n_project", label: "n8n Otomasyon Projesi" },
    { value: "ai_systems", label: "Yapay Zeka Sistemi" },
    { value: "other", label: "Diğer" }
  ];

  const projectDurations = [
    { value: "less_than_1_month", label: "1 Aydan Az" },
    { value: "1_to_3_months", label: "1-3 Ay" },
    { value: "3_to_6_months", label: "3-6 Ay" },
    { value: "more_than_6_months", label: "6 Aydan Fazla" },
    { value: "not_sure", label: "Emin Değilim" }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.phone || 
        !formData.project_type || !formData.project_duration || !formData.project_details) {
      toast.error("Lütfen tüm alanları doldurun.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        setIsSuccess(true);
        toast.success("Talebiniz başarıyla gönderildi!");
        setFormData({
          name: "",
          email: "",
          phone: "",
          project_type: "",
          project_duration: "",
          project_details: ""
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Bir hata oluştu. Lütfen tekrar deneyin.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Telefon",
      value: "+90 505 386 26 70",
      href: "tel:+905053862670"
    },
    {
      icon: Mail,
      title: "E-posta",
      value: "info@bfcodelabs.com",
      href: "mailto:info@bfcodelabs.com"
    },
    {
      icon: MapPin,
      title: "Konum",
      value: "Türkiye",
      href: null
    }
  ];

  return (
    <div className="pt-16 md:pt-20" data-testid="contact-page">
      {/* Hero */}
      <section className="py-20 bg-[#0a0a0f] relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-[#00d4aa] font-mono text-sm mb-4 block">
              {'// İLETİŞİM'}
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#f0f0f5] mb-6">
              Projenizi{" "}
              <span className="gradient-text">Konuşalım</span>
            </h1>
            <p className="text-lg text-[#9ca3af] leading-relaxed">
              Bir fikriniz mi var? Mevcut bir projenizi mi geliştirmek istiyorsunuz? 
              Formu doldurun, size en kısa sürede dönelim.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-[#12121a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-bold text-[#f0f0f5] mb-6">
                İletişim Bilgileri
              </h2>
              <p className="text-[#9ca3af] mb-8">
                Aşağıdaki bilgilerden bize ulaşabilir veya formu doldurarak 
                proje detaylarınızı paylaşabilirsiniz.
              </p>

              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-4"
                    data-testid={`contact-info-${index}`}
                  >
                    <div className="w-12 h-12 rounded-lg bg-[#16161f] border border-[#2a2a3a] flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-5 h-5 text-[#00d4aa]" />
                    </div>
                    <div>
                      <h3 className="text-[#f0f0f5] font-semibold mb-1">
                        {info.title}
                      </h3>
                      {info.href ? (
                        <a 
                          href={info.href}
                          className="text-[#9ca3af] hover:text-[#00d4aa] transition-colors"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <span className="text-[#9ca3af]">{info.value}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Working Hours */}
              <div className="mt-10 p-6 bg-[#16161f] border border-[#2a2a3a] rounded-xl">
                <h3 className="text-[#f0f0f5] font-semibold mb-4 font-mono">
                  {'// Çalışma Saatleri'}
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#9ca3af]">Pazartesi - Cuma</span>
                    <span className="text-[#f0f0f5]">09:00 - 18:00</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#9ca3af]">Cumartesi</span>
                    <span className="text-[#f0f0f5]">10:00 - 14:00</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#9ca3af]">Pazar</span>
                    <span className="text-[#00d4aa]">Kapalı</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-[#16161f] border border-[#2a2a3a] rounded-xl p-6 md:p-8">
                <h2 className="text-2xl font-bold text-[#f0f0f5] mb-6">
                  Proje Talebi Formu
                </h2>

                {isSuccess ? (
                  <div className="text-center py-12" data-testid="success-message">
                    <div className="w-16 h-16 rounded-full bg-[#00d4aa]/20 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-[#00d4aa]" />
                    </div>
                    <h3 className="text-xl font-semibold text-[#f0f0f5] mb-2">
                      Talebiniz Alındı!
                    </h3>
                    <p className="text-[#9ca3af] mb-6">
                      En kısa sürede sizinle iletişime geçeceğiz.
                    </p>
                    <Button
                      onClick={() => setIsSuccess(false)}
                      className="bg-[#00d4aa] hover:bg-[#00b894] text-[#0a0a0f]"
                      data-testid="new-request-btn"
                    >
                      Yeni Talep Oluştur
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-[#f0f0f5]">
                          Adınız Soyadınız *
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Adınız Soyadınız"
                          className="bg-[#0a0a0f] border-[#2a2a3a] text-[#f0f0f5] placeholder:text-[#9ca3af] focus:border-[#00d4aa] focus:ring-[#00d4aa]/20"
                          data-testid="input-name"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-[#f0f0f5]">
                          E-posta Adresiniz *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="ornek@email.com"
                          className="bg-[#0a0a0f] border-[#2a2a3a] text-[#f0f0f5] placeholder:text-[#9ca3af] focus:border-[#00d4aa] focus:ring-[#00d4aa]/20"
                          data-testid="input-email"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-[#f0f0f5]">
                          Telefon Numaranız *
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="+90 5XX XXX XX XX"
                          className="bg-[#0a0a0f] border-[#2a2a3a] text-[#f0f0f5] placeholder:text-[#9ca3af] focus:border-[#00d4aa] focus:ring-[#00d4aa]/20"
                          data-testid="input-phone"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-[#f0f0f5]">
                          Proje Türü *
                        </Label>
                        <Select
                          value={formData.project_type}
                          onValueChange={(value) => handleSelectChange("project_type", value)}
                        >
                          <SelectTrigger 
                            className="bg-[#0a0a0f] border-[#2a2a3a] text-[#f0f0f5] focus:border-[#00d4aa] focus:ring-[#00d4aa]/20"
                            data-testid="select-project-type"
                          >
                            <SelectValue placeholder="Proje türünü seçin" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#16161f] border-[#2a2a3a]">
                            {projectTypes.map((type) => (
                              <SelectItem 
                                key={type.value} 
                                value={type.value}
                                className="text-[#f0f0f5] focus:bg-[#00d4aa]/20 focus:text-[#f0f0f5]"
                              >
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-[#f0f0f5]">
                        Tahmini Proje Süresi *
                      </Label>
                      <Select
                        value={formData.project_duration}
                        onValueChange={(value) => handleSelectChange("project_duration", value)}
                      >
                        <SelectTrigger 
                          className="bg-[#0a0a0f] border-[#2a2a3a] text-[#f0f0f5] focus:border-[#00d4aa] focus:ring-[#00d4aa]/20"
                          data-testid="select-project-duration"
                        >
                          <SelectValue placeholder="Tahmini süreyi seçin" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#16161f] border-[#2a2a3a]">
                          {projectDurations.map((duration) => (
                            <SelectItem 
                              key={duration.value} 
                              value={duration.value}
                              className="text-[#f0f0f5] focus:bg-[#00d4aa]/20 focus:text-[#f0f0f5]"
                            >
                              {duration.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="project_details" className="text-[#f0f0f5]">
                        Proje Detayları *
                      </Label>
                      <Textarea
                        id="project_details"
                        name="project_details"
                        value={formData.project_details}
                        onChange={handleInputChange}
                        placeholder="Projeniz hakkında detaylı bilgi verin. Ne yapmak istiyorsunuz? Hedefleriniz neler?"
                        rows={5}
                        className="bg-[#0a0a0f] border-[#2a2a3a] text-[#f0f0f5] placeholder:text-[#9ca3af] focus:border-[#00d4aa] focus:ring-[#00d4aa]/20 resize-none"
                        data-testid="textarea-project-details"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-[#00d4aa] to-[#0ea5e9] hover:opacity-90 text-[#0a0a0f] font-semibold py-6"
                      data-testid="submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Gönderiliyor...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Talebi Gönder
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
